import { Metric } from './Metric';
import { MetricData, MetricDataRole } from './MetricData';
import { LocalDate } from '../../../core/pojo/date-variable';
import { MetricGroupsAndPeriods, MetricPeriod } from './MetricGroupsAndPeriods';
import { MetricCalculationService } from "../service/internal/metric-calculation.service";
import { MetricDataType } from "../../recovery/component/recovery-metrics-panel/entity/DefaultMetric";

export class MetricGroup {

  uuid: string;
  name: string;
  priority = 1;
  organizationUUID: string;
  role = MetricGroupRole.CUSTOM;
  metrics: Metric[] = [];
  // temp is being used only in the frontend. Backend must ignore this value.
  temp: {
    isSomeMetricsSelected: boolean;
    isAllMetricsSelected: boolean;
  };

  static deserializeMetricGroup(object: MetricGroup): MetricGroup {
    const group: MetricGroup = Object.assign(new MetricGroup(), object);
    group.metrics = group.metrics.map((metric) => Metric.deserializeMetric(metric));
    MetricCalculationService.setSignsForGroup(group);
    MetricCalculationService.setGroupVisibility(group);
    return group;
  }
}

export const filterMetricDataInGroupByRole = (group: MetricGroup, role: MetricDataRole): void => {
  group.metrics.forEach((metric): void => {
    metric.companyMetrics[0].metricData = metric.companyMetrics[0].metricData.filter((data) => data.role === role);
  });
};

export const getAllMetricData = (metricGroups: MetricGroup[]): MetricData[] =>
  metricGroups
    .flatMap((metricGroup) => metricGroup.metrics
      .flatMap((metric) => metric.companyMetrics
        .flatMap((companyMetric) => companyMetric.metricData
          .flatMap((metricData) => metricData))));

export const getAllRequestedMetricData = (metricGroupsAndPeriods: MetricGroupsAndPeriods): MetricData[] => {
  const extractRequestedPeriods = (periods: MetricPeriod[]): LocalDate[] =>
    periods.filter((period) => period.isRequested).map((period) => period.period);

  const actualPeriods = extractRequestedPeriods(metricGroupsAndPeriods.periods.actual);
  const budgetPeriods = extractRequestedPeriods(metricGroupsAndPeriods.periods.budget);

  const isRequestedPeriod = (periods: LocalDate[], period: LocalDate): boolean =>
    periods.includes(period);

  return metricGroupsAndPeriods.metricGroups
    .flatMap((metricGroup) => metricGroup.metrics
      .flatMap((metric) => metric.companyMetrics
        .flatMap((companyMetric) => companyMetric.metricData
          .filter((metricData) =>
            (metricData.role === MetricDataRole.ACTUAL
              ? isRequestedPeriod(actualPeriods, metricData.period)
              : isRequestedPeriod(budgetPeriods, metricData.period)),
          ),
        ),
      ),
    );
};

export const getIsMetricDataProvided = (metricGroup: MetricGroup): boolean => {
  return metricGroup.metrics
    .some((metric) => metric.companyMetrics // at least one metric
      .some((companyMetric) => companyMetric.metricData // in the company
        .some((metricData) => { // has at least one metric data
          const isMetricDocument = metric.dataProps.dataType === MetricDataType.DOCUMENT;
          if (isMetricDocument) {
            return !isMetricDataDocumentValueEmpty(metricData.rowValue); // which is not empty
          }
          return !isMetricDataRegularValueEmpty(metricData.rowValue); // which is not empty
        })));
};

export const isMetricDataRegularValueEmpty = (rowValue: string): boolean => {
  return !rowValue || rowValue === '' || rowValue === '0' || rowValue === '0.0';
};

export const isMetricDataDocumentValueEmpty = (rowValue: string): boolean => {
  return !rowValue || !rowValue.includes('\n');
};

export enum MetricGroupRole {
  SERVC = 'SerVC',
  CUSTOM = 'Custom',
}
